/**
 * VaultActivityWatcher (Task #100)
 *
 * Mounted only while the vault is unlocked. Listens for ordinary user activity
 * (pointer / keyboard / route changes) and resets the vault idle timer via
 * `bumpVaultActivity()` — throttled so we never call the timer-reset more than
 * once every 5 seconds.
 *
 * Also subscribes to `/api/user/preferences` and pushes the user's chosen
 * `vaultIdleLockMinutes` into the in-memory runtime so the idle window matches
 * the preference without requiring a re-unlock.
 *
 * No persistent storage is added or read by this component.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { bumpVaultActivity, setIdleLockMinutes } from "@/lib/vault/vaultState";

const BUMP_THROTTLE_MS = 5_000;

export default function VaultActivityWatcher() {
  const [location] = useLocation();

  const { data: prefs } = useQuery<{ vaultIdleLockMinutes?: number }>({
    queryKey: ["/api/user/preferences"],
  });

  // Push idle-window preference into the vault runtime whenever it changes.
  useEffect(() => {
    if (prefs?.vaultIdleLockMinutes) {
      setIdleLockMinutes(prefs.vaultIdleLockMinutes);
    }
  }, [prefs?.vaultIdleLockMinutes]);

  // Reset the idle timer on every route change (this is itself activity).
  useEffect(() => {
    bumpVaultActivity();
  }, [location]);

  // Global pointer / key listeners, throttled.
  useEffect(() => {
    let lastBump = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastBump < BUMP_THROTTLE_MS) return;
      lastBump = now;
      bumpVaultActivity();
    };
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    window.addEventListener("pointerdown", onActivity, opts);
    window.addEventListener("keydown", onActivity, opts);
    return () => {
      window.removeEventListener("pointerdown", onActivity, opts);
      window.removeEventListener("keydown", onActivity, opts);
    };
  }, []);

  return null;
}
